import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

// ── Types ────────────────────────────────────────────────────────────────────

interface PersonalInfo {
  fullName?: string; firstName?: string; lastName?: string
  dob?: string; gender?: string; nationality?: string; nickName?: string; age?: string
}
interface ContactDetails {
  email?: string; phone?: string; alternativeEmails?: string; phoneNumbers?: string
}
interface SocialMedia {
  linkedin?: string; facebook?: string; twitter?: string
  instagram?: string; vk?: string; socialProfiles?: string
}
interface GovernmentIDs {
  ssn?: string; tin?: string; passport?: string; driverLicense?: string; nationalID?: string
}
interface Addresses {
  street?: string; city?: string; state?: string; country?: string
  postCode?: string; latitude?: string; longitude?: string; fullAddress?: string
}
interface Financial {
  password?: string; paymentMethod?: string; balance?: string
  creditCard?: string; bankAccount?: string; financialData?: string
}
interface BreachSource {
  source: string; date: string
  password?: string; email?: string; phone?: string; address?: string
  birthDate?: string; jobTitle?: string; company?: string; socialMedia?: string
}
interface StealerLog {
  stealer_family: string; date_compromised: string; computer_name: string
  operating_system?: string; total_corporate_services: number
  total_user_services: number; top_passwords?: string[]; top_logins?: string[]
  ip?: string; domain?: string; infection_count?: number; users_affected?: number
  employees_affected?: number
}
interface SycekEnrichment {
  current_title?: string; current_employer?: string; linkedin_url?: string
  location?: string; emails?: any[]; phones?: any[]
}

export interface BTRAPIIData {
  personalInfo: PersonalInfo
  contactDetails: ContactDetails
  socialMedia: SocialMedia
  governmentIDs: GovernmentIDs
  addresses: Addresses
  financial: Financial
  photos: Array<{ url: string; source: string; type: string }>
  breachSources: BreachSource[]
  stealerLogs?: StealerLog[]
  sycekEnrichment?: SycekEnrichment[]
}

// ── Scoring engine ───────────────────────────────────────────────────────────

interface BTRAScore {
  overall: number
  layer: number
  identityAnchor: number
  accountTakeover: number
  simSwap: number
  socialPersona: number
  financialFraud: number
  activeSession: number
}

function computeBTRA(p: BTRAPIIData): BTRAScore {
  const pi = p.personalInfo
  const c = p.contactDetails
  const s = p.socialMedia
  const g = p.governmentIDs
  const f = p.financial
  const a = p.addresses

  const hasName     = !!(pi.fullName || (pi.firstName && pi.lastName))
  const hasEmail    = !!c.email
  const hasPhone    = !!(c.phone || c.phoneNumbers)
  const hasDob      = !!(pi.dob || p.breachSources.some(b => b.birthDate))
  const hasAddress  = !!(a.street || a.fullAddress || a.city)
  const hasGovtId   = !!(g.ssn || g.passport || g.driverLicense || g.nationalID || g.tin)
  const pwdBreaches = p.breachSources.filter(b => b.password && b.password.trim())
  const hasPassword = !!(f.password || pwdBreaches.length > 0)
  const hasFinancial= !!(f.creditCard || f.bankAccount)
  const socialLinks = [s.linkedin, s.facebook, s.twitter, s.instagram, s.vk].filter(Boolean)
  const socialCount = socialLinks.length
  const photoCount  = p.photos?.length || 0
  const stealers    = p.stealerLogs || []
  const enriched    = p.sycekEnrichment || []
  const jobData     = enriched.find(e => e.current_employer)

  // Layer progression
  let layer = 0
  if (hasName || hasEmail || hasPhone)                                    layer = 1
  if (layer >= 1 && hasPassword)                                          layer = 2
  if (layer >= 1 && (hasAddress || hasDob || hasGovtId))                  layer = Math.max(layer, 3)
  if (layer >= 2 && (socialCount >= 2 || photoCount > 0 || !!jobData))    layer = Math.max(layer, 4)
  if (layer >= 3 && stealers.length > 0)                                  layer = 5

  const identityAnchor = Math.min(100,
    (hasName ? 22 : 0) + (hasEmail ? 22 : 0) + (hasPhone ? 22 : 0) +
    (hasDob ? 17 : 0) + (hasAddress ? 17 : 0)
  )

  const accountTakeover = Math.min(100,
    pwdBreaches.length * 18 + (hasEmail ? 14 : 0) + socialCount * 7
  )

  const simSwap = Math.min(100,
    (hasPhone ? 40 : 0) + (hasName ? 25 : 0) + (hasAddress ? 25 : 0) + (hasDob ? 10 : 0)
  )

  const socialPersona = Math.min(100,
    socialCount * 18 + Math.min(photoCount * 6, 28) + (!!jobData ? 18 : 0)
  )

  const financialFraud = Math.min(100,
    (g.ssn ? 42 : 0) + (hasFinancial ? 30 : 0) + (hasAddress ? 14 : 0) + (hasDob ? 14 : 0)
  )

  const corpExposure = stealers.reduce((sum, sl) => sum + (sl.total_corporate_services || 0), 0)
  const activeSession = Math.min(100,
    stealers.length > 0 ? Math.min(45 + stealers.length * 12 + Math.min(corpExposure * 2, 30), 100) : 0
  )

  const overall = Math.round(
    identityAnchor * 0.24 + accountTakeover * 0.20 + simSwap * 0.20 +
    socialPersona * 0.14 + financialFraud * 0.12 + activeSession * 0.10
  )

  return { overall, layer, identityAnchor, accountTakeover, simSwap, socialPersona, financialFraud, activeSession }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const LAYER_META = [
  { label: 'NONE',     color: '#374151', desc: 'No actionable data found' },
  { label: 'PASSIVE',  color: '#eab308', desc: 'Email spoofing, social engineering, password reset attacks' },
  { label: 'CREDENTIAL', color: '#f97316', desc: 'Account takeover, credential stuffing, SSO pivot' },
  { label: 'IDENTITY', color: '#ef4444', desc: 'KYC bypass, SIM swap, financial fraud, government identity fraud' },
  { label: 'PERSONA',  color: '#dc2626', desc: 'Synthetic persona creation, spear phishing, deepfake base material' },
  { label: 'TAKEOVER', color: '#7f1d1d', desc: 'Live account access, corporate system entry, real-time impersonation' },
]

function riskLabel(score: number): { label: string; cls: string } {
  if (score >= 80) return { label: 'CRITICAL', cls: 'text-red-400' }
  if (score >= 60) return { label: 'HIGH',     cls: 'text-orange-400' }
  if (score >= 40) return { label: 'MEDIUM',   cls: 'text-yellow-400' }
  if (score >= 20) return { label: 'LOW',      cls: 'text-green-400' }
  return               { label: 'MINIMAL',  cls: 'text-gray-400' }
}

function meterColor(score: number): string {
  if (score >= 80) return 'bg-red-500'
  if (score >= 60) return 'bg-orange-500'
  if (score >= 40) return 'bg-yellow-500'
  return 'bg-green-500'
}

// ── Sub-components ───────────────────────────────────────────────────────────

function RiskMeter({ label, score }: { label: string; score: number }) {
  const rl = riskLabel(score)
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center gap-1">
        <span className="text-gray-400 font-mono text-[10px] truncate">{label}</span>
        <span className={`font-mono text-[10px] font-bold shrink-0 ${rl.cls}`}>{score}</span>
      </div>
      <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${meterColor(score)}`}
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
    </div>
  )
}

// SVG concentric impersonation risk rings
function RingVisualization({ layer, score }: { layer: number; score: BTRAScore }) {
  const cx = 180; const cy = 180
  const radii = [38, 68, 98, 128, 158]
  const ringColors = LAYER_META.slice(1).map(m => m.color)
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 360 360" style={{width:'100%', maxWidth:'110px', height:'auto', display:'block'}}>
        <defs>
          {radii.map((_r, i) => (
            <radialGradient key={i} id={`rg${i}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={ringColors[i]} stopOpacity={i < layer ? 0.35 : 0.05} />
              <stop offset="100%" stopColor={ringColors[i]} stopOpacity={0} />
            </radialGradient>
          ))}
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Background fill for active layers */}
        {radii.map((r, i) => i < layer && (
          <circle key={`bg${i}`} cx={cx} cy={cy} r={r}
            fill={`url(#rg${i})`} />
        ))}

        {/* Guide rings */}
        {radii.map((_r, i) => (
          <circle key={`guide${i}`} cx={cx} cy={cy} r={radii[i]}
            fill="none"
            stroke={i < layer ? ringColors[i] : '#1f2937'}
            strokeWidth={i < layer ? 14 : 10}
            strokeDasharray={i < layer ? 'none' : '6 8'}
            strokeOpacity={i < layer ? 0.9 : 0.3}
            filter={i < layer ? 'url(#glow)' : undefined}
          />
        ))}

        {/* Layer labels at right edge */}
        {radii.map((r, i) => (
          <text key={`lbl${i}`}
            x={cx + r + 6} y={cy + 4}
            fill={i < layer ? ringColors[i] : '#4b5563'}
            fontSize="8" fontFamily="monospace" fontWeight={i < layer ? 'bold' : 'normal'}
          >
            L{i + 1}
          </text>
        ))}

        {/* Center target */}
        <circle cx={cx} cy={cy} r={22} fill="#1a0000" stroke="#ef4444" strokeWidth={2}
          filter="url(#glow)" />
        <motion.circle cx={cx} cy={cy} r={22}
          fill="none" stroke="#ef4444" strokeWidth={1.5}
          animate={{ r: [22, 30, 22], opacity: [0.8, 0, 0.8] }}
          transition={{ duration: 2.5, repeat: Infinity }}
        />
        <text x={cx} y={cy - 4} textAnchor="middle" fill="#fca5a5" fontSize="7"
          fontFamily="monospace" fontWeight="bold">TARGET</text>
        <text x={cx} y={cy + 8} textAnchor="middle" fill="#ef4444" fontSize="10"
          fontFamily="monospace" fontWeight="bold">{score.overall}</text>

        {/* Layer name callouts on the left */}
        {radii.map((r, i) => (
          <text key={`name${i}`}
            x={cx - r - 6} y={cy + 4}
            textAnchor="end"
            fill={i < layer ? ringColors[i] : '#374151'}
            fontSize="7.5" fontFamily="monospace" fontWeight={i < layer ? 'bold' : 'normal'}
          >
            {LAYER_META[i + 1].label}
          </text>
        ))}
      </svg>
    </div>
  )
}

function Section({ title, icon, color, children, defaultOpen = true }:
  { title: string; icon: string; color: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: `${color}40` }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-white/5"
        style={{ background: `${color}0d` }}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <span className="font-mono text-sm font-bold" style={{ color }}>{title}</span>
        </div>
        <span className="text-gray-500 text-xs">{open ? '▲' : '▼'}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-4 space-y-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function Anchor({ label, value, confidence, note }:
  { label: string; value: string | undefined; confidence?: string; note?: string }) {
  const present = !!value
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${present ? 'bg-black/60 border border-orange-500/25' : 'bg-black/30 border border-gray-800'}`}>
      <span className={`mt-0.5 font-mono text-sm font-bold ${present ? 'text-orange-400' : 'text-gray-700'}`}>
        {present ? '✓' : '○'}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`font-mono text-xs ${present ? 'text-gray-300' : 'text-gray-600'}`}>{label}</span>
          {confidence && present && (
            <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${
              confidence === 'HIGH' ? 'bg-red-900/50 text-red-300' :
              confidence === 'MEDIUM' ? 'bg-yellow-900/50 text-yellow-300' :
              'bg-gray-800 text-gray-400'
            }`}>{confidence}</span>
          )}
        </div>
        {present && value && (
          <div className="text-white text-sm font-mono mt-1 truncate">{value}</div>
        )}
        {note && present && (
          <div className="text-gray-500 text-xs mt-1">{note}</div>
        )}
        {!present && <div className="text-gray-700 text-xs mt-1">Not found in breach data</div>}
      </div>
    </div>
  )
}

// ── Main component ───────────────────────────────────────────────────────────

interface BTRAPanelProps {
  piiData: BTRAPIIData
  query: string
}

export function BTRAPanel({ piiData, query }: BTRAPanelProps) {
  const [adversaryRevealed, setAdversaryRevealed] = useState(false)
  const score = useMemo(() => computeBTRA(piiData), [piiData])

  const pi      = piiData.personalInfo
  const c       = piiData.contactDetails
  const s       = piiData.socialMedia
  const g       = piiData.governmentIDs
  const f       = piiData.financial
  const a       = piiData.addresses
  const photos  = piiData.photos || []
  const stealers= piiData.stealerLogs || []
  const enriched= piiData.sycekEnrichment || []
  const jobInfo = enriched.find(e => e.current_employer)

  const pwdBreaches = piiData.breachSources.filter(b => b.password && b.password.trim())
  const socialLinks = Object.entries(s)
    .filter(([k, v]) => k !== 'socialProfiles' && !!v)
    .map(([k, v]) => ({ platform: k.charAt(0).toUpperCase() + k.slice(1), url: v as string }))

  // Top threats generator
  const topThreats = useMemo(() => {
    const threats: string[] = []
    if (stealers.length > 0) {
      const corp = stealers.reduce((s, l) => s + (l.total_corporate_services || 0), 0)
      threats.push(`Active stealer infection${stealers.length > 1 ? `s (${stealers.length} devices)` : ''} — corporate system exposure${corp > 0 ? ` across ${corp} services` : ''}`)
    }
    if (score.simSwap >= 70) {
      threats.push(`SIM swap viable — carrier identity verification data present (name${c.phone ? ', phone' : ''}${a.city ? ', billing address' : ''} confirmed)`)
    }
    if (pwdBreaches.length > 0) {
      threats.push(`${pwdBreaches.length} service${pwdBreaches.length > 1 ? 's' : ''} with exposed credential${pwdBreaches.length > 1 ? 's' : ''} — password reuse attack surface active`)
    }
    if (score.identityAnchor >= 75 && !stealers.length && !pwdBreaches.length) {
      threats.push('Complete identity anchor set — sufficient for social engineering and password reset attacks across major platforms')
    }
    if (g.ssn) {
      threats.push('Government ID fragment exposed — KYC bypass and financial fraud risk elevated to CRITICAL')
    }
    if (socialLinks.length >= 3 || photos.length >= 3) {
      threats.push('Rich social persona available — sufficient for synthetic identity creation and targeted spear phishing')
    }
    return threats.slice(0, 3)
  }, [score, piiData])

  // Remediation actions
  const remediation = useMemo(() => {
    const steps: string[] = []
    if (c.phone || c.phoneNumbers) steps.push('Contact mobile carrier — enable number lock / SIM port freeze immediately')
    if (pwdBreaches.length > 0) steps.push(`Rotate credentials on ${pwdBreaches.map(b => b.source).join(', ')} and any services sharing those passwords`)
    if (stealers.length > 0) steps.push('Full device wipe and credential rotation required — active stealer infection confirmed')
    if (c.email) steps.push('Enable FIDO2 hardware security key on primary email account (blocks takeover even with stolen credentials)')
    if (g.ssn) steps.push('Place fraud alert and credit freeze with all major bureaus — SSN fragment exposed')
    if (socialLinks.length > 0) steps.push(`Audit and minimize personal details on ${socialLinks.map(l => l.platform).join(', ')} — reduce persona reconstruction surface`)
    return steps.slice(0, 5)
  }, [piiData])

  const overallRisk = riskLabel(score.overall)

  // Always render — show minimal state when layer=0
  const [collapsed, setCollapsed] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-xl border border-red-900/40 bg-gradient-to-b from-black via-red-950/10 to-black shadow-[0_0_40px_rgba(239,68,68,0.08)] overflow-hidden"
    >
      {/* Header — click anywhere to collapse/expand */}
      <div
        className="bg-gradient-to-r from-red-950/80 via-black to-red-950/80 border-b border-red-900/40 px-4 py-3 cursor-pointer select-none"
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-red-400 font-mono text-[9px] tracking-widest mb-0.5">BREACH THREAT & RISK ASSESSMENT</div>
            <div className="text-white font-mono text-sm font-bold truncate">Attack Surface — <span className="text-red-300">{query}</span></div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-right">
              <div className={`font-mono text-2xl font-bold leading-none ${overallRisk.cls}`}>{score.overall}</div>
              <div className={`font-mono text-[9px] font-bold tracking-widest ${overallRisk.cls}`}>{overallRisk.label}</div>
            </div>
            <svg
              className={`w-4 h-4 text-red-500/60 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {!collapsed && (
      <>
      {/* Insufficient data fallback */}
      {score.layer === 0 && (
        <div className="px-4 py-4 space-y-2">
          <div className="text-gray-600 font-mono text-[10px] uppercase tracking-widest">Assessment Status</div>
          <div className="text-amber-400 font-mono text-xs">Insufficient breach data for full BTRA assessment</div>
          <div className="text-gray-600 font-mono text-xs leading-relaxed">
            No personal attributes (name, email, phone) were extracted from breach records. Run the Deep BTRA Investigation below to enrich from live pivot sources.
          </div>
        </div>
      )}

      <div className={`p-4 space-y-4 ${score.layer === 0 ? 'opacity-30 pointer-events-none select-none' : ''}`}>

        {/* Ring Visualization + Sub-scores — side by side */}
        <div className="flex items-start gap-3">

          {/* Ring viz — fixed width, compact */}
          <div className="w-[110px] shrink-0 flex flex-col items-center">
            <RingVisualization layer={score.layer} score={score} />
          </div>

          {/* Sub-scores — fill remaining width */}
          <div className="flex-1 min-w-0 space-y-2">
            <div className="text-gray-600 font-mono text-[9px] tracking-widest mb-1">ATTACK SURFACE</div>
            <RiskMeter label="Identity Anchor"    score={score.identityAnchor}  />
            <RiskMeter label="Account Takeover"   score={score.accountTakeover} />
            <RiskMeter label="SIM Swap"           score={score.simSwap}         />
            <RiskMeter label="Social Persona"     score={score.socialPersona}   />
            <RiskMeter label="Financial Fraud"    score={score.financialFraud}  />
            <RiskMeter label="Active Session"     score={score.activeSession}   />
          </div>
        </div>

        {/* Layer badge — full width, below ring + meters */}
        {score.layer > 0 && (
          <div className="rounded-lg border px-4 py-2 font-mono text-sm font-bold text-center"
            style={{
              borderColor: LAYER_META[score.layer].color,
              color: LAYER_META[score.layer].color,
              background: `${LAYER_META[score.layer].color}18`,
            }}>
            LAYER {score.layer} — {LAYER_META[score.layer].label} IMPERSONATION
          </div>
        )}
        {score.layer > 0 && (
          <div className="text-gray-400 font-mono text-xs text-center">
            {LAYER_META[score.layer].desc}
          </div>
        )}

        {/* Top Threats — compact strip */}
        {topThreats.length > 0 && (
          <div className="bg-red-950/20 border border-red-900/40 rounded-lg px-3 py-2.5 space-y-1.5">
            <div className="text-red-500 font-mono text-[9px] font-bold tracking-widest">TOP THREATS</div>
            {topThreats.map((t, i) => (
              <div key={i} className="flex gap-2 text-[11px] font-mono text-gray-400 leading-snug">
                <span className="text-red-600 shrink-0">{i + 1}.</span>
                <span>{t}</span>
              </div>
            ))}
          </div>
        )}

        {/* Identity Anchors */}
        <Section title="SECTION 1 — IDENTITY ANCHORS" icon="🪪" color="#f97316" defaultOpen={false}>
          <div className="text-gray-500 font-mono text-xs mb-3">
            Attributes an adversary uses to prove they are the subject to a third party (banks, carriers, platforms)
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Anchor label="Full Name"       value={pi.fullName || [pi.firstName, pi.lastName].filter(Boolean).join(' ')}
              confidence={pi.fullName ? 'HIGH' : 'MEDIUM'} note={`Confirmed in ${piiData.breachSources.length} breach source${piiData.breachSources.length !== 1 ? 's' : ''}`} />
            <Anchor label="Date of Birth"   value={pi.dob || piiData.breachSources.find(b => b.birthDate)?.birthDate}
              confidence="HIGH" note="Usable for SIM swap and financial KYC" />
            <Anchor label="Primary Email"   value={c.email}
              confidence="HIGH" note={pwdBreaches.length > 0 ? `${pwdBreaches.length} breach${pwdBreaches.length !== 1 ? 'es' : ''} with credentials` : 'Deliverable — password reset risk'} />
            <Anchor label="Phone Number"    value={c.phone || c.phoneNumbers}
              confidence="HIGH" note="SIM swap attack vector confirmed" />
            <Anchor label="Physical Address" value={a.fullAddress || [a.street, a.city, a.state, a.country].filter(Boolean).join(', ')}
              confidence={a.street ? 'HIGH' : 'MEDIUM'} note="Carrier billing verification data" />
            <Anchor label="Government ID"   value={g.ssn ? `SSN: ****-**-${g.ssn.slice(-4)}` : g.passport ? `Passport: ${g.passport}` : g.nationalID || g.driverLicense || g.tin}
              confidence="CRITICAL" note="KYC bypass and financial fraud risk CRITICAL" />
          </div>
          <div className="mt-3 pt-3 border-t border-orange-900/40">
            <div className="font-mono text-xs">
              <span className="text-gray-500">ANCHOR STRENGTH: </span>
              <span className={`font-bold ${riskLabel(score.identityAnchor).cls}`}>{riskLabel(score.identityAnchor).label}</span>
              {score.simSwap >= 70 && (
                <span className="text-red-400 ml-3">— Sufficient for SIM swap attack</span>
              )}
              {score.identityAnchor >= 80 && (
                <span className="text-red-400 ml-3">— Sufficient for financial identity fraud</span>
              )}
            </div>
          </div>
        </Section>

        {/* Account Takeover Surface */}
        <Section title="SECTION 2 — ACCOUNT TAKEOVER SURFACE" icon="🔑" color="#ef4444" defaultOpen={false}>
          <div className="text-gray-500 font-mono text-xs mb-3">
            Services accessible directly via exposed credentials or via password reset using identity anchors
          </div>

          {pwdBreaches.length > 0 && (
            <div className="mb-4">
              <div className="text-red-400 font-mono text-xs font-bold mb-2 tracking-wider">DIRECT CREDENTIAL ACCESS RISK</div>
              <div className="space-y-2">
                {pwdBreaches.map((b, i) => (
                  <div key={i} className="flex items-center justify-between bg-red-950/30 border border-red-900/40 rounded-lg p-3">
                    <div>
                      <div className="text-white font-mono text-sm">{b.source}</div>
                      <div className="text-red-400 font-mono text-xs mt-0.5">
                        Password exposed · {b.date || 'Unknown date'}
                        {b.password && b.password.length > 0 && b.password.length < 20 && ' · Likely plaintext'}
                        {b.password && b.password.length >= 60 && ' · Hash — check for reuse pattern'}
                      </div>
                    </div>
                    <div className="text-red-300 font-mono text-xs font-bold">HIGH</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-orange-400 font-mono text-xs font-bold mb-2 tracking-wider">PASSWORD RESET ATTACK SURFACE</div>
            <div className="space-y-1.5">
              {c.email && (
                <div className="flex items-start gap-2 text-xs font-mono text-gray-300">
                  <span className="text-orange-400 shrink-0">▸</span>
                  <span>Gmail / Google services — email-based recovery: deliverable address confirmed. Compromise unlocks Google SSO chain (Drive, Workspace, GCP consoles).</span>
                </div>
              )}
              {(c.phone || c.phoneNumbers) && (
                <div className="flex items-start gap-2 text-xs font-mono text-gray-300">
                  <span className="text-orange-400 shrink-0">▸</span>
                  <span>SMS-based 2FA on all services linked to verified phone — OTP interception viable via SIM swap.</span>
                </div>
              )}
              {pi.dob && c.email && a.city && (
                <div className="flex items-start gap-2 text-xs font-mono text-gray-300">
                  <span className="text-red-500 shrink-0">▸</span>
                  <span>Bank / financial portals — "forgot password" often requires name + DOB + email or postal code. All three present in breach data.</span>
                </div>
              )}
              {s.linkedin && (
                <div className="flex items-start gap-2 text-xs font-mono text-gray-300">
                  <span className="text-orange-400 shrink-0">▸</span>
                  <span>LinkedIn — email-based recovery: email confirmed deliverable. Professional network access enables BEC and spear phishing.</span>
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* SIM Swap */}
        {score.simSwap >= 40 && (
          <Section title="SECTION 4 — SIM SWAP READINESS" icon="📱" color="#a855f7" defaultOpen={false}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="text-purple-400 font-mono text-xs font-bold tracking-wider">CARRIER VERIFICATION DATA</div>
                <div className="space-y-1">
                  {[
                    { item: 'Full name', present: !!(pi.fullName || (pi.firstName && pi.lastName)) },
                    { item: 'Phone number', present: !!(c.phone || c.phoneNumbers) },
                    { item: 'Billing address', present: !!(a.street || a.city) },
                    { item: 'Date of birth', present: !!pi.dob },
                    { item: 'Security Q: employer', present: !!(jobInfo?.current_employer) },
                    { item: 'Security Q: location', present: !!(a.city || a.country) },
                  ].map(({ item, present }) => (
                    <div key={item} className="flex items-center gap-2 font-mono text-xs">
                      <span className={present ? 'text-green-400' : 'text-gray-700'}>
                        {present ? '✓' : '○'}
                      </span>
                      <span className={present ? 'text-gray-300' : 'text-gray-700'}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-purple-950/30 border border-purple-900/40 rounded-lg p-3">
                <div className="text-purple-400 font-mono text-xs font-bold mb-2">ASSESSMENT</div>
                <div className="text-gray-300 font-mono text-xs leading-relaxed">
                  {score.simSwap >= 80
                    ? 'Carrier social engineering is viable. An adversary with this data can likely pass tier-1 carrier identity verification and take control of the phone number.'
                    : score.simSwap >= 60
                    ? 'Partial carrier verification data present. SIM swap success probability moderate — additional social engineering required.'
                    : 'Insufficient data for high-confidence SIM swap. Phone number exposed but supporting verification data limited.'}
                </div>
                <div className={`mt-3 font-mono text-xs font-bold ${score.simSwap >= 70 ? 'text-red-400' : 'text-yellow-400'}`}>
                  SIM Swap Risk: {score.simSwap >= 80 ? 'CRITICAL' : score.simSwap >= 60 ? 'HIGH' : 'MEDIUM'}
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* Social Persona */}
        {score.socialPersona >= 20 && (
          <Section title="SECTION 3 — SOCIAL PERSONA" icon="🎭" color="#60a5fa" defaultOpen={false}>
            <div className="text-gray-500 font-mono text-xs mb-3">
              What an adversary can construct to impersonate the subject convincingly to their contacts
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="space-y-1.5">
                {socialLinks.map(({ platform, url }) => (
                  <div key={platform} className="flex items-center gap-2 bg-blue-950/20 border border-blue-900/30 rounded px-3 py-2">
                    <span className="text-blue-400 font-mono text-xs font-bold w-16 shrink-0">{platform}</span>
                    <span className="text-gray-300 font-mono text-xs truncate">{url}</span>
                  </div>
                ))}
                {photos.length > 0 && (
                  <div className="flex items-center gap-2 bg-blue-950/20 border border-blue-900/30 rounded px-3 py-2">
                    <span className="text-blue-400 font-mono text-xs font-bold w-16 shrink-0">Photos</span>
                    <span className="text-gray-300 font-mono text-xs">{photos.length} image{photos.length !== 1 ? 's' : ''} indexed — usable as deepfake base material</span>
                  </div>
                )}
                {jobInfo && (
                  <div className="flex items-center gap-2 bg-blue-950/20 border border-blue-900/30 rounded px-3 py-2">
                    <span className="text-blue-400 font-mono text-xs font-bold w-16 shrink-0">Employment</span>
                    <span className="text-gray-300 font-mono text-xs">{jobInfo.current_employer}{jobInfo.current_title ? ` · ${jobInfo.current_title}` : ''}</span>
                  </div>
                )}
              </div>
              <div className="bg-blue-950/20 border border-blue-900/30 rounded-lg p-3">
                <div className="text-blue-400 font-mono text-xs font-bold mb-2">SPEAR PHISHING ASSESSMENT</div>
                <div className="text-gray-300 font-mono text-xs leading-relaxed">
                  {score.socialPersona >= 70
                    ? 'An attacker has sufficient context to craft a convincing message appearing to come from this person — using their real name, real photos, knowledge of real relationships, and plausible employment context.'
                    : score.socialPersona >= 40
                    ? 'Moderate social persona available. Spear phishing possible with known associates and platform presence.'
                    : 'Limited social persona data. Targeted impersonation requires additional OSINT.'}
                </div>
                <div className={`mt-2 font-mono text-xs font-bold ${score.socialPersona >= 70 ? 'text-red-400' : score.socialPersona >= 40 ? 'text-orange-400' : 'text-yellow-400'}`}>
                  Spear Phishing Difficulty: {score.socialPersona >= 70 ? 'LOW' : score.socialPersona >= 40 ? 'MEDIUM' : 'HIGH'}
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* Financial Fraud */}
        {score.financialFraud >= 20 && (
          <Section title="SECTION 5 — FINANCIAL FRAUD SURFACE" icon="💳" color="#22c55e" defaultOpen={false}>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Full legal name',      present: !!(pi.fullName || pi.firstName), note: 'Required for bank KYC' },
                { label: 'Date of birth',        present: !!(pi.dob || piiData.breachSources.some(b => b.birthDate)), note: 'Required for identity verification' },
                { label: 'Current address',      present: !!(a.street || a.city), note: 'Required for account creation and KYC' },
                { label: 'Phone (OTP target)',   present: !!(c.phone || c.phoneNumbers), note: 'OTP interception via SIM swap' },
                { label: 'SSN / National ID',    present: !!(g.ssn || g.nationalID), note: 'Highest-risk financial fraud enabler' },
                { label: 'Payment card data',    present: !!(f.creditCard), note: 'Partial card exposure in breach data' },
                { label: 'Bank account data',    present: !!(f.bankAccount), note: 'Direct account access risk' },
              ].map(({ label, present, note }) => (
                <div key={label} className={`flex items-start gap-2 p-2 rounded text-xs font-mono ${
                  present
                    ? 'bg-green-950/25 border border-green-900/30'
                    : 'bg-black/30 border border-gray-800/50 opacity-50'
                }`}>
                  <span className={present ? 'text-green-400 shrink-0' : 'text-gray-700 shrink-0'}>
                    {present ? '✓' : '○'}
                  </span>
                  <div>
                    <div className={present ? 'text-gray-200' : 'text-gray-600'}>{label}</div>
                    {present && <div className="text-gray-500 text-xs mt-0.5">{note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Active Session / Stealer Logs */}
        {stealers.length > 0 && (
          <Section title="SECTION 6 — ACTIVE SESSION RISK (STEALER LOGS)" icon="💀" color="#dc2626" defaultOpen={false}>
            <div className="text-red-400 font-mono text-xs mb-3 font-bold">
              CRITICAL — Device malware infection confirmed. Credentials may be actively accessible.
            </div>
            <div className="space-y-3">
              {stealers.map((sl, i) => (
                <div key={i} className="bg-red-950/30 border border-red-900/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-red-300 font-mono text-sm font-bold">{sl.stealer_family || 'Unknown Stealer'}</div>
                    <div className="text-red-500 font-mono text-xs">{sl.date_compromised || 'Date unknown'}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                    {sl.computer_name && <div><span className="text-gray-600">Device: </span><span className="text-gray-300">{sl.computer_name}</span></div>}
                    {sl.operating_system && <div><span className="text-gray-600">OS: </span><span className="text-gray-300">{sl.operating_system}</span></div>}
                    {sl.total_corporate_services > 0 && <div><span className="text-gray-600">Corporate services: </span><span className="text-red-300 font-bold">{sl.total_corporate_services}</span></div>}
                    {sl.total_user_services > 0 && <div><span className="text-gray-600">User services: </span><span className="text-orange-300">{sl.total_user_services}</span></div>}
                    {sl.ip && <div><span className="text-gray-600">IP at infection: </span><span className="text-gray-300">{sl.ip}</span></div>}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Adversary View — gated */}
        <div className="border border-red-900/60 rounded-lg overflow-hidden">
          <div className="bg-red-950/40 border-b border-red-900/40 px-4 py-3 flex items-center gap-3">
            <span className="text-red-400 text-lg">👁</span>
            <div>
              <div className="text-red-400 font-mono text-xs font-bold tracking-widest">SECTION 6 — ADVERSARY VIEW</div>
              <div className="text-gray-500 font-mono text-xs">Assembled profile as an attacker would use it</div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {!adversaryRevealed ? (
              <motion.div
                key="gate"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-8 flex flex-col items-center text-center gap-4"
              >
                <div className="text-gray-600 font-mono text-4xl">⬛⬛⬛⬛⬛⬛⬛⬛⬛⬛⬛<br />⬛⬛⬛⬛⬛⬛⬛⬛⬛⬛⬛</div>
                <div className="text-gray-300 font-mono text-sm max-w-md">
                  This panel shows exactly what an adversary's working dossier looks like — all exposed data assembled as an attacker would use it.
                </div>
                <div className="text-gray-500 font-mono text-xs max-w-sm">
                  Intended for security briefings. The moment a stakeholder sees their profile assembled this way is the most memorable security briefing they will attend.
                </div>
                <button
                  onClick={() => setAdversaryRevealed(true)}
                  className="mt-2 px-6 py-3 bg-red-900/60 hover:bg-red-800/60 border border-red-700/60 text-red-300 font-mono text-sm font-bold rounded-lg transition-colors"
                >
                  I UNDERSTAND — REVEAL ADVERSARY VIEW
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="revealed"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-5"
              >
                <div className="bg-black border border-red-900/60 rounded-lg p-5 font-mono text-sm space-y-4">
                  <div className="text-red-500 text-xs font-bold tracking-widest mb-3">// ADVERSARY WORKING DOSSIER — {query.toUpperCase()}</div>

                  {/* Identity block */}
                  <div className="space-y-1 text-gray-300">
                    {(pi.fullName || (pi.firstName && pi.lastName)) && (
                      <div><span className="text-gray-600">Target:    </span>{pi.fullName || `${pi.firstName} ${pi.lastName}`}</div>
                    )}
                    {pi.dob && <div><span className="text-gray-600">DOB:       </span>{pi.dob}</div>}
                    {(c.phone || c.phoneNumbers) && <div><span className="text-gray-600">Phone:     </span>{c.phone || c.phoneNumbers}</div>}
                    {c.email && <div><span className="text-gray-600">Email:     </span>{c.email}</div>}
                    {(a.fullAddress || a.street || a.city) && (
                      <div><span className="text-gray-600">Address:   </span>{a.fullAddress || [a.street, a.city, a.state, a.country].filter(Boolean).join(', ')}</div>
                    )}
                  </div>

                  {/* Employment */}
                  {jobInfo && (
                    <div className="pt-2 border-t border-gray-900 space-y-1 text-gray-300">
                      <div><span className="text-gray-600">Works at:  </span>{jobInfo.current_employer}{jobInfo.current_title ? ` — ${jobInfo.current_title}` : ''}</div>
                      {jobInfo.linkedin_url && <div><span className="text-gray-600">LinkedIn:  </span>{jobInfo.linkedin_url}</div>}
                    </div>
                  )}

                  {/* Social */}
                  {socialLinks.length > 0 && (
                    <div className="pt-2 border-t border-gray-900 space-y-1 text-gray-300">
                      {socialLinks.map(({ platform, url }) => (
                        <div key={platform}><span className="text-gray-600">{platform.padEnd(10, ' ')}</span>{url}</div>
                      ))}
                    </div>
                  )}

                  {/* Credentials */}
                  {pwdBreaches.length > 0 && (
                    <div className="pt-2 border-t border-gray-900 space-y-1">
                      <div className="text-red-500 text-xs font-bold">// EXPOSED CREDENTIALS</div>
                      {pwdBreaches.map((b, i) => (
                        <div key={i} className="text-gray-300">
                          <span className="text-gray-600">{b.source.padEnd(14, ' ')}</span>
                          <span className="text-red-300">{b.password && b.password.length <= 30 ? b.password : '[hash/encrypted]'}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Govt IDs */}
                  {(g.ssn || g.passport || g.nationalID || g.driverLicense) && (
                    <div className="pt-2 border-t border-gray-900 space-y-1">
                      <div className="text-red-500 text-xs font-bold">// GOVERNMENT ID FRAGMENTS</div>
                      {g.ssn && <div className="text-red-300">SSN fragment: {g.ssn}</div>}
                      {g.passport && <div className="text-red-300">Passport: {g.passport}</div>}
                      {g.nationalID && <div className="text-red-300">National ID: {g.nationalID}</div>}
                      {g.driverLicense && <div className="text-red-300">DL: {g.driverLicense}</div>}
                    </div>
                  )}

                  {/* Photos */}
                  {photos.length > 0 && (
                    <div className="pt-2 border-t border-gray-900">
                      <div className="text-gray-600 text-xs mb-2">// INDEXED PROFILE IMAGES ({photos.length})</div>
                      <div className="flex flex-wrap gap-2">
                        {photos.slice(0, 6).map((ph, i) => (
                          <img key={i} src={ph.url} alt="" className="w-12 h-12 rounded object-cover border border-gray-800 opacity-80"
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                        ))}
                        {photos.length > 6 && <div className="w-12 h-12 rounded border border-gray-800 flex items-center justify-center text-gray-600 text-xs">+{photos.length - 6}</div>}
                      </div>
                    </div>
                  )}

                  {/* Source attribution */}
                  <div className="pt-2 border-t border-gray-900 text-gray-700 text-xs">
                    DATA SOURCES: {piiData.breachSources.length} breach database{piiData.breachSources.length !== 1 ? 's' : ''}
                    {stealers.length > 0 ? `, ${stealers.length} stealer log${stealers.length !== 1 ? 's' : ''}` : ''}
                    {socialLinks.length > 0 ? `, ${socialLinks.length} social platform${socialLinks.length !== 1 ? 's' : ''}` : ''}
                    {photos.length > 0 ? `, ${photos.length} indexed image${photos.length !== 1 ? 's' : ''}` : ''}
                  </div>
                </div>

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => setAdversaryRevealed(false)}
                    className="px-4 py-2 border border-gray-700 text-gray-400 font-mono text-xs rounded hover:bg-gray-900 transition-colors"
                  >
                    REDACT VIEW
                  </button>
                  <div className="flex-1 bg-blue-950/20 border border-blue-900/30 rounded px-4 py-2 text-blue-400 font-mono text-xs flex items-center">
                    Share With Subject — use "Export Risk Brief" button in header to generate a personal risk briefing
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Remediation */}
        {remediation.length > 0 && (
          <div className="bg-black/50 border border-green-900/40 rounded-lg p-4">
            <div className="text-green-400 font-mono text-xs font-bold tracking-wider mb-3">REMEDIATION PRIORITY LIST</div>
            <div className="space-y-2">
              {remediation.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 shrink-0 border border-gray-700 rounded text-gray-700 flex items-center justify-center text-xs mt-0.5">
                    {i + 1}
                  </div>
                  <div className="text-gray-300 font-mono text-xs">{step}</div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
      </>
      )} {/* end !collapsed */}
    </motion.div>
  )
}
